<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900 pb-12">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12">
      <!-- Breadcrumb -->
      <nav v-if="product" class="flex items-center gap-2 text-sm mb-8">
        <RouterLink to="/" class="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
          Home
        </RouterLink>
        <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
        </svg>
        <RouterLink to="/products" class="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
          Products
        </RouterLink>
        <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
        </svg>
        <span class="text-gray-900 dark:text-white font-medium">{{ product.name }}</span>
      </nav>
      <!-- Loading State -->
      <div v-if="loading" class="text-center py-12">
        <div class="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
        <p class="mt-4 text-gray-600">Loading product...</p>
      </div>

      <!-- Error State -->
      <div v-else-if="error || !product" class="text-center py-12">
        <p class="text-red-600 mb-4">{{ error || 'Product not found' }}</p>
        <RouterLink to="/products" class="text-blue-600 hover:underline">Back to Products</RouterLink>
      </div>

      <!-- Product Detail -->
      <div v-else class="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
        <!-- Product Images -->
        <div class="space-y-4">
          <!-- Main Image -->
          <div class="relative rounded-3xl overflow-hidden aspect-square group">
            <!-- Botón lupa (solo responsive): abre imagen a pantalla completa -->
            <button
              type="button"
              @click="showZoomModal = true"
              class="lg:hidden absolute bottom-4 right-4 z-20 w-12 h-12 bg-white/95 dark:bg-gray-800/95 rounded-full flex items-center justify-center shadow-lg hover:bg-white dark:hover:bg-gray-800 transition-all cursor-pointer border border-gray-200 dark:border-gray-700"
              aria-label="View image larger"
            >
              <svg class="w-6 h-6 text-gray-700 dark:text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </button>
            <!-- Clic en la imagen (solo responsive) también abre el zoom -->
            <div
              class="lg:hidden absolute inset-0 z-[5] cursor-zoom-in"
              @click="showZoomModal = true"
              aria-hidden="true"
            />
            <!-- Navigation arrows (only show if more than one image) -->
            <button
              v-if="productImages.length > 1"
              @click.stop="prevImage"
              class="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white/90 dark:bg-gray-800/90 rounded-full flex items-center justify-center shadow-lg hover:bg-white dark:hover:bg-gray-800 transition-all opacity-75 hover:opacity-100 cursor-pointer"
              aria-label="Previous image"
            >
              <svg class="w-6 h-6 text-gray-900 dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            <button
              v-if="productImages.length > 1"
              @click.stop="nextImage"
              class="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white/90 dark:bg-gray-800/90 rounded-full flex items-center justify-center shadow-lg hover:bg-white dark:hover:bg-gray-800 transition-all opacity-75 hover:opacity-100 cursor-pointer"
              aria-label="Next image"
            >
              <svg class="w-6 h-6 text-gray-900 dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>
            
            <!-- Image counter (only show if more than one image) -->
            <div
              v-if="productImages.length > 1"
              class="absolute top-6 right-6 z-10 px-3 py-1 bg-black/50 text-white text-sm font-semibold rounded-full"
            >
              {{ selectedImageIndex + 1 }} / {{ productImages.length }}
            </div>
            
            <Transition
              name="fade"
              mode="out-in"
            >
              <img 
                :key="currentImage"
                :src="currentImage" 
                :alt="`${product.name} - Image ${selectedImageIndex + 1}`"
                class="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500 pointer-events-none"
                @error="(e: any) => e.target.src = '/logos/isolateprotein-coldbrew.webp'"
              />
            </Transition>
            
            <!-- Badges: más pequeños en responsive para no tapar el artículo -->
            <div class="absolute top-3 left-3 sm:top-4 sm:left-4 md:top-6 md:left-6 flex flex-col gap-1 sm:gap-2 z-10">
              <span v-if="product.badge" class="px-2 py-0.5 text-[10px] sm:px-3 sm:py-1 sm:text-xs md:px-4 md:py-2 font-bold text-white bg-gradient-to-r from-gray-800 to-black rounded-full">
                {{ product.badge }}
              </span>
              <span class="px-2 py-0.5 text-[10px] sm:px-3 sm:py-1 sm:text-xs md:px-4 md:py-2 font-bold text-white bg-green-600 rounded-full">
                In Stock
              </span>
            </div>
          </div>

          <!-- Thumbnail Images (only show if more than one image). Responsive: 4 columns, less gap; lg: one row. -->
          <div 
            v-if="productImages.length > 1" 
            class="grid grid-cols-4 gap-1.5 sm:gap-2 lg:flex lg:flex-nowrap lg:gap-4"
          >
            <div 
              v-for="(image, index) in productImages" 
              :key="index"
              @click="selectImage(index)"
              :class="[
                'rounded-lg lg:rounded-xl overflow-hidden cursor-pointer transition-all aspect-square max-h-14 sm:max-h-16 lg:h-24 lg:w-24 lg:max-h-none flex-shrink-0',
                selectedImageIndex === index 
                  ? 'ring-4 ring-green-600 dark:ring-green-500 opacity-100 scale-105' 
                  : 'hover:ring-2 hover:ring-gray-900 dark:hover:ring-white opacity-70 hover:opacity-100'
              ]"
            >
              <img 
                :src="image" 
                :alt="`${product?.name} thumbnail ${index + 1}`"
                class="w-full h-full object-cover transition-opacity"
                @error="(e: any) => e.target.src = '/logos/isolateprotein-coldbrew.webp'"
              />
            </div>
          </div>
        </div>

        <!-- Product Info -->
        <div class="space-y-6">
          <!-- Title & Rating -->
          <div>
            <h1 class="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-2">
              {{ product.name }}
            </h1>
            <p class="text-xl text-gray-600 dark:text-gray-400 mb-4">
              {{ product.description }}
            </p>
            
            <!-- Flavor & Servings -->
            <div class="flex flex-wrap gap-3 mb-6">
              <div class="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-800 rounded-xl">
                <span class="text-sm font-semibold text-gray-500 dark:text-gray-400">Flavor</span>
                <span class="text-sm font-bold text-gray-900 dark:text-white">{{ product.flavor || 'Unflavored' }}</span>
              </div>
              <div class="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-800 rounded-xl">
                <span class="text-sm font-semibold text-gray-500 dark:text-gray-400">Servings</span>
                <span class="text-sm font-bold text-gray-900 dark:text-white">{{ product.servingSize || '30' }}</span>
              </div>
            </div>
          </div>

          <!-- Price -->
          <div class="border-t border-b border-gray-200 dark:border-gray-800 py-6">
            <div class="flex items-baseline gap-4">
              <span class="text-5xl font-bold text-gray-900 dark:text-white">
                ${{ product.price.toFixed(2) }}
              </span>
            </div>
          </div>



          <!-- Quantity & Add to Cart -->
          <div class="space-y-4">
            <div class="flex items-center gap-4">
              <label class="text-sm font-semibold" style="color: #b38a58;">
                Quantity:
              </label>
              <div class="flex items-center gap-2 bg-gray-100 dark:bg-gray-800 rounded-xl p-2">
                <button 
                  @click="quantity > 1 && quantity--"
                  class="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                >
                  <svg class="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 12H4" />
                  </svg>
                </button>
                <span class="w-12 text-center font-bold text-gray-900 dark:text-white text-lg">
                  {{ quantity }}
                </span>
                <button 
                  @click="quantity++"
                  class="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                >
                  <svg class="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                  </svg>
                </button>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="flex gap-4">
              <button 
                @click="addToCart"
                class="flex-1 py-4 font-bold rounded-xl transition-colors duration-300 shadow-lg flex items-center justify-center gap-2 cursor-pointer"
                style="background-color: #EDD97A; color: #1a1200;"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
                <span>Add to Cart</span>
              </button>
              <button class="w-14 h-14 flex items-center justify-center bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-xl transition-colors cursor-pointer">
                <svg class="w-6 h-6 text-gray-900 dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </button>
            </div>
          </div>

          <!-- Trust Badges: iconos y texto más pequeños en responsive -->
          <div class="grid grid-cols-3 gap-2 sm:gap-3 md:gap-4 pt-4 sm:pt-6 border-t border-gray-200 dark:border-gray-800">  
           <div class="text-center">
            <div class="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-1 sm:mb-2">
              <svg class="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-red-600 dark:text-red-400" fill="currentColor" viewBox="0 0 24 24">
                <path d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
              </svg>
            </div>
            <p class="text-[10px] sm:text-xs font-semibold text-gray-900 dark:text-white">Made in USA</p>
          </div>
               <div class="text-center">
              <div class="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-1 sm:mb-2">
                <svg class="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <p class="text-[10px] sm:text-xs font-semibold text-gray-900 dark:text-white">No Artificial Ingredients</p>
            </div>  
       
       <div class="text-center">
          <div class="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-1 sm:mb-2">
            <svg class="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 4c-3 0-8 1.5-11 5s-4 9-4 9l1 1s5-1 9-4 5-8 5-11z M6 19l-3 3" />
            </svg>
          </div>
          <p class="text-[10px] sm:text-xs font-semibold text-gray-900 dark:text-white">Non-GMO</p>
        </div>
  
          </div>
        </div>
      </div>

      <!-- Modal lupa: imagen a pantalla completa (solo útil en responsive, pero visible en todos) -->
      <Teleport to="body">
        <Transition name="zoom-modal">
          <div
            v-if="showZoomModal"
            class="fixed inset-0 z-[100] flex flex-col bg-black/95"
            @click.self="showZoomModal = false"
          >
            <button
              type="button"
              @click="showZoomModal = false"
              class="absolute top-4 right-4 z-[101] w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors cursor-pointer"
              aria-label="Close"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div class="flex-1 flex items-center justify-center p-4 min-h-0">
              <img 
                :src="currentImage" 
                :alt="`${product?.name} - Image ${selectedImageIndex + 1}`"
                class="max-w-full max-h-full w-auto h-auto object-contain select-none"
                @click.stop
                @error="(e: any) => e.target.src = '/logos/isolateprotein-coldbrew.webp'"
              />
            </div>
            <div v-if="productImages.length > 1" class="flex justify-center gap-4 pb-8">
              <button
                type="button"
                @click.stop="prevImage"
                class="w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white cursor-pointer"
                aria-label="Anterior"
              >
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <span class="flex items-center text-white/80 text-sm font-medium">{{ selectedImageIndex + 1 }} / {{ productImages.length }}</span>
              <button
                type="button"
                @click.stop="nextImage"
                class="w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white cursor-pointer"
                aria-label="Siguiente"
              >
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </Transition>
      </Teleport>

      <!-- Similar Products -->
      <div class="mt-16">
        <div class="flex items-center justify-between mb-8">
          <h2 class="text-3xl font-bold text-gray-900 dark:text-white">
            Similar Products
          </h2>
          <RouterLink 
            to="/products" 
            class="font-semibold transition-colors flex items-center gap-2"
            style="color: #5f4842;"
          >
            <span>View All</span>
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </RouterLink>
        </div>

        <!-- Similar Products Grid -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div 
            v-for="similarProduct in similarProducts" 
            :key="similarProduct.id"
            @click="goToProduct(similarProduct.id)"
            class="bg-white dark:bg-gray-800 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-200 dark:border-gray-700 group cursor-pointer"
          >
            <div class="relative bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 p-6 h-64 flex items-center justify-center overflow-hidden">
              <img 
                :src="similarProduct.image" 
                :alt="similarProduct.name"
                class="w-full h-full object-contain transform group-hover:scale-110 transition-transform duration-500"
              />
            </div>
            
            <div class="p-6">
              <h3 class="text-lg font-bold text-gray-900 dark:text-white mb-2 truncate">
                {{ similarProduct.name }}
              </h3>
              <p class="text-sm text-gray-600 dark:text-gray-400 mb-4 truncate">
                {{ similarProduct.description }}
              </p>
              
              <div class="flex items-center gap-2 mb-4">
                <div class="flex gap-1">
                  <svg v-for="i in 5" :key="i" class="w-4 h-4 text-yellow-400 fill-current" viewBox="0 0 20 20">
                    <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z"/>
                  </svg>
                </div>
                <span class="text-sm text-gray-600 dark:text-gray-400">4.8</span>
              </div>
              
              <div class="flex items-center justify-between">
                <p class="text-2xl font-bold text-gray-900 dark:text-white">
                  ${{ similarProduct.price.toFixed(2) }}
                </p>
                <button 
                  @click.stop="addSimilarToCart(similarProduct.id)"
                  class="px-4 py-2 font-semibold rounded-xl transition-colors duration-300"
                  style="background-color: #EDD97A; color: #1a1200;"
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted, watchEffect } from 'vue'
import { useRoute, useRouter, RouterLink } from 'vue-router'
import { useCartStore } from '@/stores/cart'
import { useProducts } from '@/composables/useProducts'
import { mapApiProductToEcommerce, mapApiProductToCartItem } from '@/utils/productMapper'
import type { ApiProduct } from '@/types/api-product'
import { getImageUrl } from '@/utils/imageUrl'

const route = useRoute()
const router = useRouter()
const cartStore = useCartStore()

const quantity = ref(1)
const loading = ref(false)
const error = ref<string | null>(null)
const selectedImageIndex = ref(0)
const showZoomModal = ref(false)

// Bloquear scroll del body cuando el modal lupa está abierto
watchEffect(() => {
  if (typeof document === 'undefined') return
  if (showZoomModal.value) {
    document.body.style.overflow = 'hidden'
  } else {
    document.body.style.overflow = ''
  }
})

// Get all product images
const productImages = computed(() => {
  if (!currentApiProduct.value) return []
  
  const images = currentApiProduct.value.images || []
  
  // Si hay imágenes de la API, usarlas (normalizar por si vienen con localhost)
  if (images.length > 0) {
    return images.map(img => getImageUrl(img.Image_url))
  }
  
  // Si no hay imágenes, usar la imagen del mapper como fallback
  if (product.value?.image) {
    return [product.value.image]
  }
  
  return []
})

// Current displayed image
const currentImage = computed(() => {
  const images = productImages.value
  if (images.length === 0) return '/logos/isolateprotein-coldbrew.webp'
  return images[selectedImageIndex.value] || images[0]
})

// Methods to navigate images
const nextImage = () => {
  if (productImages.value.length > 1) {
    selectedImageIndex.value = (selectedImageIndex.value + 1) % productImages.value.length
  }
}

const prevImage = () => {
  if (productImages.value.length > 1) {
    selectedImageIndex.value = (selectedImageIndex.value - 1 + productImages.value.length) % productImages.value.length
  }
}

const selectImage = (index: number) => {
  selectedImageIndex.value = index
}

// Products from API
const { products: apiProducts, fetchProducts, getProductById } = useProducts()

const productId = computed(() => {
  const id = route.params.id as string
  const parsed = parseInt(id)
  return isNaN(parsed) ? 0 : parsed
})

// Current product
const currentApiProduct = ref<ApiProduct | null>(null)
const product = computed(() => {
  if (currentApiProduct.value) {
    const mapped = mapApiProductToEcommerce(currentApiProduct.value)
    // Asegurar que siempre tenga una imagen válida
    if (!mapped.image || mapped.image === '' || mapped.image === '/logos/placeholder.webp') {
      // Asignar imagen basada en el ID del producto
      const imageIndex = (currentApiProduct.value.Product_id - 1) % 12
      const defaultImages = [
        '/logos/isolateprotein-coldbrew.webp',
        '/logos/Creatine_30_serv_render.webp',
        '/logos/essential_pre-_champion_mentality.webp',
        '/logos/eaaplus-pineapple.webp',
        '/logos/REPLENISH_LEMON_LIME_FRONT.webp',
        '/logos/FUEL-Orange-Front_48a9211d-cc14-489a-8ebf-13bf3af02482.webp',
        '/logos/ACETYL_L_CARN_NEW_FRONT_b06488b7-fc9d-4baf-b68e-191985ac0132.webp',
        '/logos/itholate-championmentality.webp',
        '/logos/thavage_-_champion_mentality.webp',
        '/logos/thupertavage-championmentality.webp',
        '/logos/cookiesandcream-rtd.webp',
        '/logos/Raw_Lightweight_Cap.webp'
      ]
      mapped.image = defaultImages[imageIndex] || defaultImages[0]
    }
    return mapped
  }
  return null
})

// Similar products (other products from the same category or all products)
const similarProducts = computed(() => {
  if (!product.value) return []
  return apiProducts.value
    .filter(p => p.Product_id !== productId.value)
    .slice(0, 4)
    .map(mapApiProductToEcommerce)
})

const addToCart = () => {
  if (!currentApiProduct.value) return
  
  const cartItem = mapApiProductToCartItem(currentApiProduct.value)
  for (let i = 0; i < quantity.value; i++) {
    cartStore.addItem(cartItem)
  }
  quantity.value = 1
}

const addSimilarToCart = (productIdStr: string) => {
  const apiProduct = apiProducts.value.find(p => p.Product_id.toString() === productIdStr)
  if (apiProduct) {
    const cartItem = mapApiProductToCartItem(apiProduct)
    cartStore.addItem(cartItem)
  }
}

const goToProduct = (productIdStr: string) => {
  router.push(`/product/${productIdStr}`)
  if (typeof window !== 'undefined') {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
}

// Cargar producto por ID (usado en mount y cuando cambia la ruta)
async function loadProduct(id: number) {
  if (!id) {
    error.value = 'Invalid product ID'
    return
  }
  loading.value = true
  error.value = null
  currentApiProduct.value = null
  selectedImageIndex.value = 0
  try {
    await fetchProducts()
    const foundProduct = apiProducts.value.find(p => p.Product_id === id)
    if (foundProduct) {
      currentApiProduct.value = foundProduct
    } else {
      currentApiProduct.value = await getProductById(id)
    }
  } catch (err: unknown) {
    const e = err as { response?: { status?: number } }
    if (e.response?.status === 404) {
      error.value = 'Product not found'
    } else {
      error.value = err instanceof Error ? err.message : 'Error loading product. Please try again later.'
    }
  } finally {
    loading.value = false
  }
}

// Recargar producto cuando cambia el ID en la URL (ej: click en Similar Products)
watch(
  () => route.params.id,
  async (newId) => {
    const id = typeof newId === 'string' ? parseInt(newId, 10) : 0
    if (id && id !== currentApiProduct.value?.Product_id) {
      await loadProduct(id)
      if (typeof window !== 'undefined') {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    }
  },
  { immediate: false }
)

// Keyboard navigation for images
const handleKeydown = (event: KeyboardEvent) => {
  if (productImages.value.length <= 1) return
  
  if (event.key === 'ArrowLeft') {
    event.preventDefault()
    prevImage()
  } else if (event.key === 'ArrowRight') {
    event.preventDefault()
    nextImage()
  }
}

onMounted(() => {
  if (typeof window !== 'undefined') {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    window.addEventListener('keydown', handleKeydown)
  }
  loadProduct(productId.value)
})

// Cleanup keyboard listener y scroll body
onUnmounted(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('keydown', handleKeydown)
  }
  if (typeof document !== 'undefined') {
    document.body.style.overflow = ''
  }
})
</script>

<style scoped>
/* Fade transition for images */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>

<style>
/* Transición del modal lupa (no scoped para Teleport) */
.zoom-modal-enter-active,
.zoom-modal-leave-active {
  transition: opacity 0.2s ease;
}
.zoom-modal-enter-from,
.zoom-modal-leave-to {
  opacity: 0;
}
</style>